import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, X } from "lucide-react";
import { mh } from "@/lib/mh";
import { money } from "@/lib/onb";
import { errText } from "@/lib/http";
import { Panel, Eyebrow, timeAgo } from "@/components/mh/bits";
import { Btn } from "@/components/mh/controls";

const KIND = { rejected: ["Rejected", "#FF3B5C"], edited: ["Edited", "#FFB020"], blocked: ["Blocked", "#FF3B5C"], gate_failed: ["Gate failed", "#00E5FF"] };

export const SuggestionDiff = ({ s, onDecided, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const decide = async (accept) => {
    setBusy(true);
    try { await (accept ? mh.acceptSuggestion(s.id) : mh.dismissSuggestion(s.id)); toast.success(accept ? "Improvement applied. Next briefs use it" : "Suggestion dismissed"); onDecided(); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const added = s.proposed.guardrails.filter((g) => !s.current.guardrails.includes(g));
  const removed = s.current.guardrails.filter((g) => !s.proposed.guardrails.includes(g));
  return (
    <div className="rounded-xl border border-[#D4FF3A]/30 bg-[#D4FF3A]/[0.03] p-4 space-y-3 mh-enter" data-testid={`suggestion-${s.id}`}>
      <div className="flex flex-wrap items-center gap-2"><Sparkles className="w-4 h-4 text-[#D4FF3A]" /><span className="text-sm font-medium">{s.summary}</span>
        <span className="font-mono text-[10px] text-muted-foreground">{s.trigger === "auto" ? "auto" : "requested"} · from {s.signals.length} signal{s.signals.length === 1 ? "" : "s"} · {timeAgo(s.created_at)}</span></div>
      <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">{s.changes.map((c) => <li key={c}>{c}</li>)}</ul>
      {(added.length > 0 || removed.length > 0) && (
        <div className="font-mono text-[11px] space-y-0.5" data-testid={`suggestion-guardrails-${s.id}`}>
          {added.map((g) => <div key={g} className="text-[#D4FF3A]">+ {g}</div>)}
          {removed.map((g) => <div key={g} className="text-[#FF3B5C] line-through">− {g}</div>)}
        </div>
      )}
      <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Revised operating notes</summary><p className="whitespace-pre-wrap mt-2">{s.proposed.prompt_addendum}</p></details>
      {canEdit && <div className="flex gap-2">
        <Btn variant="primary" onClick={() => decide(true)} disabled={busy} testId={`suggestion-accept-${s.id}`}><Check className="w-4 h-4" />Accept</Btn>
        <Btn onClick={() => decide(false)} disabled={busy} testId={`suggestion-dismiss-${s.id}`}><X className="w-4 h-4" />Dismiss</Btn>
      </div>}
    </div>
  );
};

export const LearningPanel = ({ a, canEdit }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => mh.learning(a.slug).then((r) => setD(r.data)).catch(() => {}), [a.slug]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const pending = d.suggestions.filter((s) => s.status === "pending");
  const drafting = d.suggestions.some((s) => s.status === "drafting");
  const act = d.actual;
  const v = a.tailored?.value;
  const rate = v?.hourly_cost || 50;
  const ask = async () => { setBusy(true); try { await mh.suggest(a.slug); toast.success("Suggestion drafted"); load(); } catch (e) { toast.error(errText(e)); } setBusy(false); };
  return (
    <Panel cls="p-5 space-y-4" testId="learning-panel">
      <div className="grid sm:grid-cols-[1fr_1.4fr] gap-5">
        <div data-testid="role-actual">
          <Eyebrow>Projected vs actual · last 30 days</Eyebrow>
          <div className="flex items-end gap-4 mt-2">
            <div><div className="font-display text-3xl text-[#D4FF3A]" data-testid="role-actual-money">{money((act?.minutes || 0) / 60 * rate)}</div><div className="font-mono text-[10px] text-muted-foreground">actual · {((act?.minutes || 0) / 60).toFixed(1)} h</div></div>
            {v && <div><div className="font-display text-3xl text-muted-foreground">{money(v.money_low)}–{money(v.money_high)}</div><div className="font-mono text-[10px] text-muted-foreground">projected · {v.hours_low}–{v.hours_high} h</div></div>}
          </div>
          <p className="font-mono text-[10px] text-muted-foreground mt-2">{act ? `${act.receipts} receipts · ${act.counted} counted · ${act.rejected} rejected · ${act.edited} edited · ${act.unreported} without receipt${act.capped ? ` · ${act.capped} capped` : ""}` : "No runs yet"}</p>
        </div>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Eyebrow>Learning loop</Eyebrow>
            {canEdit && <Btn onClick={ask} disabled={busy || drafting || pending.length > 0 || d.open_signals === 0} testId="suggest-btn"><Sparkles className="w-4 h-4" />{busy || drafting ? "Drafting…" : "Suggest improvements"}</Btn>}
          </div>
          <p className="font-mono text-[10px] text-muted-foreground mt-1" data-testid="learning-open-signals">{d.open_signals} new signal{d.open_signals === 1 ? "" : "s"} · auto-suggests at {d.threshold}</p>
          <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto" data-testid="learning-signals">
            {d.signals.slice(0, 8).map((s) => (
              <li key={s.id} className="text-xs flex gap-2"><span className="font-mono text-[10px] flex-shrink-0 w-16" style={{ color: KIND[s.kind][1] }}>{KIND[s.kind][0]}</span>
                <span className={`truncate ${s.used ? "text-muted-foreground" : ""}`} title={s.detail}>{s.detail}</span></li>
            ))}
            {d.signals.length === 0 && <li className="text-xs text-muted-foreground">No feedback yet. Rejections, desk edits, blocked receipts and failed gates show up here.</li>}
          </ul>
        </div>
      </div>
      {pending.map((s) => <SuggestionDiff key={s.id} s={s} onDecided={load} canEdit={canEdit} />)}
    </Panel>
  );
};
